import { useMemo, useSyncExternalStore } from "react";
import { useRouter as useNextRouter } from "next/navigation";
import { getUtm, subscribeUtm } from "./store";
import {
  REFERRER_PARAM,
  toUtmCodes,
  withUtmParams,
  type UtmCodes,
} from "./params";

const noUtm = () => "";

/** The session's UTM query. Empty during SSR and hydration, so server and client markup agree. */
export function useUtm(): string {
  return useSyncExternalStore(subscribeUtm, getUtm, noUtm);
}

export function useUtmHref(href: string): string {
  const utm = useUtm();
  return utm ? withUtmParams(href, utm, window.location.href) : href;
}

/** `next/navigation`'s router, with `push`/`replace` keeping the session's UTMs. */
export function useRouter(): ReturnType<typeof useNextRouter> {
  const router = useNextRouter();
  return useMemo(
    () => ({
      ...router,
      push: (href, options) => router.push(withSessionUtm(href), options),
      replace: (href, options) => router.replace(withSessionUtm(href), options),
    }),
    [router],
  );
}

/** UTM codes for a CRM lead: the session's campaign plus the page it was submitted from. */
export function useUtmCodes(): UtmCodes {
  const utm = useUtm();
  return useMemo(
    () =>
      toUtmCodes(
        utm,
        typeof window === "undefined"
          ? ""
          : `${window.location.origin}${window.location.pathname}${window.location.search}`,
      ),
    [utm],
  );
}

/** The session's referrer; empty when the visit has none. */
export function useReferrer(): string {
  return new URLSearchParams(useUtm()).get(REFERRER_PARAM) ?? "";
}

function withSessionUtm(href: string): string {
  return withUtmParams(href, getUtm(), window.location.href);
}
